import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader } from '@/components/ui/Card';
import { Spinner, LoadingScreen } from '@/components/ui/Spinner';
import { needsOnboarding } from '@/api/onboarding';
import { getStoredSession } from '@/api/authStorage';

export function SignupPage() {
  const { signUp, isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={needsOnboarding(user) ? '/accept-terms' : '/dashboard'} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await signUp(email.trim(), password);
      if (err) {
        setError(err.message);
        return;
      }
      const nextUser = getStoredSession()?.user;
      navigate(needsOnboarding(nextUser) ? '/accept-terms' : '/dashboard', { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page px-4 py-12">
      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-5">
            <ProductBrand variant="light" showTagline={false} />
            <div>
              <h1 className="text-headline-md font-semibold text-foreground">Create account</h1>
              <p className="mt-2 text-body-sm text-foreground-muted leading-relaxed">
                Sign up with email and password or continue with Google. This is for the app — not your Amazon
                Advertising account.
              </p>
            </div>

            {error ? (
              <div className="rounded-xl bg-error-subtle border border-error/20 px-3 py-2 text-body-sm text-error-ink">
                {error}
              </div>
            ) : null}

            <GoogleSignInButton onError={(msg) => setError(msg || null)} />

            <div className="relative flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-body-sm text-foreground-muted">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-body-sm font-medium text-foreground mb-1">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-body-sm font-medium text-foreground mb-1">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-foreground-muted hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-body-sm text-foreground-muted">At least 8 characters</p>
              </div>
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? <Spinner className="w-4 h-4" /> : null}
              {submitting ? 'Creating account…' : 'Create account'}
            </Button>

            <p className="text-center text-body-sm text-foreground-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-brand-deep hover:text-brand hover:underline">
                Sign in
              </Link>
            </p>
          </CardHeader>
        </Card>
      </form>
    </div>
  );
}
